import { gsap } from 'gsap';

/**
 * Accordéon FAQ : ouverture exclusive et animée.
 *
 * Le balisage reste en <details>/<summary> (cf. views/acf-layouts/faq.twig) :
 * sans JS, l'accordéon fonctionne nativement, au clavier compris. Avec JS, on
 * intercepte le clic pour animer la hauteur et refermer la question ouverte.
 *
 * On n'utilise volontairement PAS l'attribut `name` des <details> (accordéon
 * exclusif natif) : le navigateur refermerait l'autre panneau instantanément,
 * ce qui entrerait en conflit avec l'animation de fermeture.
 *
 * Contrat de balisage :
 *  - [data-faq-accordion] : le conteneur de la liste
 *  - [data-faq-item]      : chaque <details>
 *  - [data-faq-panel]     : le bloc réponse, cible de l'animation
 */

const DURATION = 0.4;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const duration = () => (prefersReducedMotion() ? 0 : DURATION);

export function initFaqAccordion(context = document) {
  const groups = context.querySelectorAll('[data-faq-accordion]');
  if (!groups.length) return;

  groups.forEach((group) => {
    if (group.__faqAccordion) return; // garde contre la double init
    group.__faqAccordion = true;

    const items = Array.from(group.querySelectorAll('[data-faq-item]'))
      .map((el) => ({
        details: el,
        summary: el.querySelector('summary'),
        panel: el.querySelector('[data-faq-panel]'),
      }))
      .filter((item) => item.summary && item.panel);

    items.forEach((item) => {
      // État initial : le panneau ouvert garde sa hauteur naturelle
      if (item.details.open) {
        gsap.set(item.panel, { height: 'auto', opacity: 1 });
      } else {
        gsap.set(item.panel, { height: 0, opacity: 0, overflow: 'hidden' });
      }

      item.summary.addEventListener('click', (event) => {
        event.preventDefault(); // on pilote l'attribut `open` nous-mêmes
        if (item.details.open) {
          closeItem(item);
        } else {
          items.forEach((other) => {
            if (other !== item && other.details.open) closeItem(other);
          });
          openItem(item);
        }
      });
    });
  });
}

function openItem(item) {
  const { details, panel } = item;
  gsap.killTweensOf(panel);

  // `open` d'abord : sans lui le contenu reste hors flux et non mesurable
  details.open = true;
  const target = panel.scrollHeight;

  gsap.fromTo(
    panel,
    { height: 0, opacity: 0, overflow: 'hidden' },
    {
      height: target,
      opacity: 1,
      duration: duration(),
      ease: 'power2.out',
      onComplete: () => {
        // Hauteur libérée : le contenu peut grandir sans être coupé
        panel.style.height = 'auto';
        panel.style.overflow = 'visible';
      },
    }
  );
}

function closeItem(item) {
  const { details, panel } = item;
  gsap.killTweensOf(panel);

  const current = panel.getBoundingClientRect().height;

  gsap.fromTo(
    panel,
    { height: current, opacity: 1, overflow: 'hidden' },
    {
      height: 0,
      opacity: 0,
      duration: duration(),
      ease: 'power2.inOut',
      // `open` retiré seulement à la fin, sinon le contenu disparaît d'un coup
      onComplete: () => {
        details.open = false;
      },
    }
  );
}
